import * as React from "react"
import { AlertTriangle, Landmark, Loader2, PowerOff } from "lucide-react"

import type { Account } from "@/types"
import { formatCurrency } from "@/lib/utils/formatCurrency"
import { Badge } from "@/shared/ui/atoms/Badge"
import { Button } from "@/shared/ui/atoms/Button"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/atoms/AlertDialog"

interface ConfirmDeleteAccountDialogProps {
  isOpen: boolean
  account: Account | null
  isDeleting: boolean
  onConfirm: () => void
  onCancel: () => void
}

export const ConfirmDeleteAccountDialog = React.memo(function ConfirmDeleteAccountDialog({
  isOpen,
  account,
  isDeleting,
  onConfirm,
  onCancel,
}: ConfirmDeleteAccountDialogProps) {
  if (!account) return null

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => { if (!open) onCancel() }}>
      <AlertDialogContent className="sm:max-w-md gap-5 border-amber-500/30 shadow-2xl">
        <AlertDialogHeader className="gap-3">
          <div className="flex items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 ring-8 ring-amber-500/5">
              <AlertTriangle className="size-5" />
            </div>
            <div className="flex flex-col text-left">
              <AlertDialogTitle className="text-base sm:text-lg">
                ¿Desactivar cuenta?
              </AlertDialogTitle>
              <p className="text-xs text-muted-foreground">
                La cuenta quedará archivada y no estará disponible para nuevos movimientos.
              </p>
            </div>
          </div>
        </AlertDialogHeader>

        {/* Account Details Card */}
        <div className="flex flex-col gap-2.5 rounded-lg border border-border/70 bg-muted/30 p-3.5 text-xs">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Landmark className="size-4 text-muted-foreground shrink-0" />
              <span className="font-semibold text-foreground text-sm truncate">
                {account.name}
              </span>
            </div>
            <Badge variant="outline" className="text-[11px] shrink-0 font-medium">
              {account.is_savings_account ? "Ahorro" : "Disponible"}
            </Badge>
          </div>

          <div className="flex items-center justify-between pt-1 border-t border-border/40 text-muted-foreground">
            <span>Saldo registrado:</span>
            <span className="font-mono font-bold text-foreground text-xs">
              {formatCurrency(Number(account.current_balance || 0), account.currency)}
            </span>
          </div>
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed">
          <strong>Tranquilo:</strong> Todo el historial de ingresos, gastos y transferencias asociados a esta cuenta se mantendrá seguro y reflejado en los totales históricos.
        </p>

        <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isDeleting}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting}
            className="w-full sm:w-auto min-w-[130px]"
          >
            {isDeleting ? (
              <>
                <Loader2 className="size-4 animate-spin mr-2" />
                Desactivando…
              </>
            ) : (
              <>
                <PowerOff className="size-4 mr-2" />
                Desactivar cuenta
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
})
